import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { send } from '@/shared/messages';
import type { SearchHit } from '@/shared/types';
import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';

// Full-text search across every chapter in the active test. The heavy lifting
// is in the backend (Dexie scan in the extension, Postgres FTS in the webapp);
// this is purely the search box + result list. Clicking a hit opens that
// chapter (which shows its summary).
export function SearchRoute({
  activeTestId,
  onOpenChapter,
}: {
  activeTestId: string | null;
  onOpenChapter: (chapterId: string, query: string) => void;
}) {
  // Seed from ?q= so a shared/bookmarked search URL restores the query.
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(() => searchParams.get('q') ?? '');
  const [results, setResults] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Monotonic id so a slow response from an earlier keystroke can't clobber a
  // newer one.
  const reqId = useRef(0);

  // Mirror the trimmed query into ?q= (replace, so typing doesn't pile up
  // history). The equality guard keeps this from looping on its own update.
  useEffect(() => {
    const trimmed = query.trim();
    const current = searchParams.get('q') ?? '';
    if (trimmed === current) return;
    const next = new URLSearchParams(searchParams);
    if (trimmed) next.set('q', trimmed);
    else next.delete('q');
    setSearchParams(next, { replace: true });
    // Only react to query edits; searchParams/setSearchParams are stable enough
    // and re-including them would risk a feedback loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!activeTestId || trimmed === '') {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    const id = ++reqId.current;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const r = await send({ type: 'SEARCH_TEST', testId: activeTestId, query: trimmed });
        if (id !== reqId.current) return; // a newer query superseded this one
        setResults(r.results);
        setError(null);
      } catch (e) {
        if (id !== reqId.current) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (id === reqId.current) setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [query, activeTestId]);

  const trimmed = query.trim();

  return (
    <div className="space-y-3 p-4">
      <div className="space-y-1">
        <h2 className="text-sm font-semibold text-slate-800">Search this test</h2>
        <p className="text-xs text-slate-600">
          Search across every chapter's text and video transcripts in the active test to find
          where something was covered.
        </p>
      </div>

      <input
        type="search"
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search chapters…"
        className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
      />

      {!activeTestId && (
        <div className="text-xs text-slate-500">Select a test to search its chapters.</div>
      )}

      {activeTestId && trimmed !== '' && (
        <div className="text-xs text-slate-500">
          {loading
            ? 'Searching…'
            : `${results.length} ${results.length === 1 ? 'chapter' : 'chapters'} matched`}
        </div>
      )}

      {error && <Alert>{error}</Alert>}

      <ul className="space-y-2">
        {results.map((hit) => (
          <li key={hit.chapterId}>
            <button
              onClick={() => onOpenChapter(hit.chapterId, trimmed)}
              className="block w-full rounded border border-slate-200 bg-card p-3 text-left hover:border-blue-300 hover:bg-blue-50"
            >
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-slate-800">{hit.chapterTitle}</span>
                {hit.sourceType === 'video' && <Badge>▶ video</Badge>}
              </div>
              {hit.snippet && (
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  {highlight(hit.snippet, trimmed)}
                </p>
              )}
            </button>
          </li>
        ))}
      </ul>

      {activeTestId && trimmed !== '' && !loading && results.length === 0 && !error && (
        <div className="text-xs text-slate-500">No chapters matched “{trimmed}”.</div>
      )}
    </div>
  );
}

// highlight wraps occurrences of any query term in the snippet with <mark>.
// Works for both backends since it re-highlights from the raw query terms
// (the server's ts_headline markers are stripped to keep snippets plain).
function highlight(snippet: string, query: string): ReactNode {
  const terms = query
    .toLowerCase()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map(escapeRegExp);
  if (terms.length === 0) return snippet;

  // Split on a capturing group so the matched terms land in the array; a
  // non-global, anchored regex tests each part (no shared lastIndex state).
  const splitRe = new RegExp(`(${terms.join('|')})`, 'gi');
  const matchRe = new RegExp(`^(?:${terms.join('|')})$`, 'i');
  return snippet.split(splitRe).map((part, i) =>
    matchRe.test(part) ? (
      <mark key={i} className="rounded bg-yellow-200 px-0.5 text-slate-900">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
